/**
 * Defensive parsing for backend/API response payloads.
 *
 * Different endpoints (and backend versions) wrap collections differently:
 * a raw array, `{responseData: [...]}` (ApiResponse envelope), `{data: [...]}`,
 * `{content: [...], totalElements}` (Spring Page), `{devices: [...]}` — and
 * occasionally with snake_case key mutations (`device_count` vs `deviceCount`).
 * A missed key silently yields `[]`/`0` and the UI renders an empty list with
 * a wrong count. These helpers unwrap tolerantly and log the payload schema
 * when nothing usable is found, so the mismatch is diagnosable from the
 * console instead of invisible.
 */

const DEFAULT_LIST_KEYS = [
  "responseData",
  "content",
  "data",
  "devices",
  "items",
  "records",
  "results",
];

/** snake_case → camelCase ("device_count" → "deviceCount"). */
const toCamel = (k) => k.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

/** camelCase → snake_case ("deviceCount" → "device_count"). */
const toSnake = (k) => k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

/**
 * Case-mutation-tolerant field read: tries the key as given, its camelCase
 * and snake_case variants, and finally a case-insensitive match.
 */
export function getField(obj, key) {
  if (obj == null || typeof obj !== "object") return undefined;
  for (const k of [key, toCamel(key), toSnake(key)]) {
    if (k in obj) return obj[k];
  }
  const lower = key.toLowerCase().replace(/_/g, "");
  const hit = Object.keys(obj).find((k) => k.toLowerCase().replace(/_/g, "") === lower);
  return hit !== undefined ? obj[hit] : undefined;
}

/** One-level schema sketch of a payload for console.debug inspection. */
export function describeSchema(payload) {
  if (Array.isArray(payload)) return `Array(${payload.length})`;
  if (payload == null || typeof payload !== "object") return String(typeof payload);
  return (
    "{ " +
    Object.entries(payload)
      .map(([k, v]) =>
        Array.isArray(v) ? `${k}: Array(${v.length})` : `${k}: ${v === null ? "null" : typeof v}`
      )
      .join(", ") +
    " }"
  );
}

/**
 * Extract the first array found in a payload, trying the payload itself, the
 * preferred wrapper keys (with case-mutation tolerance), and one nested level
 * (e.g. `response.data.data`, `responseData.content`).
 *
 * @param {*} payload   parsed response body (e.g. axios `response.data`)
 * @param {string[]} preferredKeys wrapper keys to try, in priority order
 * @param {string} context label used in diagnostics logging
 * @returns {Array} the extracted list, or [] (never undefined)
 */
export function extractList(payload, preferredKeys = DEFAULT_LIST_KEYS, context = "payload") {
  if (Array.isArray(payload)) return payload;

  if (payload && typeof payload === "object") {
    for (const key of preferredKeys) {
      const v = getField(payload, key);
      if (Array.isArray(v)) return v;
      // One nested level: {data: {devices: [...]}} / {responseData: {content: [...]}}
      if (v && typeof v === "object") {
        for (const inner of preferredKeys) {
          const nested = getField(v, inner);
          if (Array.isArray(nested)) return nested;
        }
      }
    }
  }

  console.debug(
    `[responsePayload] No list found in ${context}. Raw payload schema:`,
    describeSchema(payload)
  );
  return [];
}

/**
 * Extract a numeric count, trying the given keys with case-mutation
 * tolerance; falls back to `fallbackList.length` so the UI count is always
 * derived from the data actually rendered, never a stuck/unmapped variable.
 */
export function extractCount(payload, keys, fallbackList = []) {
  if (payload && typeof payload === "object") {
    for (const key of keys) {
      const v = getField(payload, key);
      const n = typeof v === "string" ? Number(v) : v;
      if (typeof n === "number" && Number.isFinite(n)) return n;
    }
  }
  return fallbackList.length;
}
